import React, { useState } from 'react';
import { NewAppLayout } from '@/components/new-ux/layout/NewAppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, ChevronDown, Mail, Users, Database, Bot, Target, Zap, ArrowRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const automationTemplates = [
  {
    id: 1,
    title: "Find, qualify, and enrich local businesses",
    description: "Comprehensive workflow for local business prospecting and qualification",
    author: "Werner Maurits",
    category: "Lead Generation",
    useCase: "Local Business",
    provider: "Clay + Openmart",
    icon: Target,
    color: "bg-orange-500",
    tags: ["Lead Gen", "Local", "Enrichment"],
    difficulty: "Intermediate"
  },
  {
    id: 2,
    title: "Use Autobound enrichment to prepare for discovery calls",
    description: "Automate research preparation for sales discovery calls using AI",
    author: "Werner Maurits", 
    category: "Sales Prep",
    useCase: "Discovery Calls",
    provider: "Autobound + Clay",
    icon: Users,
    color: "bg-blue-500",
    tags: ["Sales Prep", "AI Research", "Discovery"],
    difficulty: "Beginner"
  },
  {
    id: 3,
    title: "Automate email data cleaning and hygiene",
    description: "Clean and validate email lists for better deliverability",
    author: "Chris Viglietta",
    category: "Data Hygiene",
    useCase: "Email Validation",
    provider: "Clay",
    icon: Mail,
    color: "bg-green-600",
    tags: ["Data Cleaning", "Email", "Validation"],
    difficulty: "Beginner"
  },
  {
    id: 4,
    title: "Scrape public databases with Clay's Chrome scraper",
    description: "Extract data from public sources using automated scraping",
    author: "Chris Viglietta",
    category: "Data Collection",
    useCase: "Web Scraping",
    provider: "Clay Chrome",
    icon: Database,
    color: "bg-purple-500",
    tags: ["Scraping", "Data Collection", "Chrome"],
    difficulty: "Advanced"
  },
  {
    id: 5,
    title: "Enrich and score event signups",
    description: "Automatically score and qualify event registrations",
    author: "Marketing Team",
    category: "Event Management",
    useCase: "Event Scoring",
    provider: "Clay + AI",
    icon: Bot,
    color: "bg-indigo-600",
    tags: ["Events", "Scoring", "AI"],
    difficulty: "Intermediate"
  },
  {
    id: 6,
    title: "Netflix Gotham's next wanted",
    description: "Advanced prospecting workflow for entertainment industry",
    author: "Netflix Team",
    category: "Industry Specific",
    useCase: "Entertainment",
    provider: "Custom",
    icon: Zap,
    color: "bg-pink-500",
    tags: ["Entertainment", "Custom", "Advanced"],
    difficulty: "Advanced"
  },
  {
    id: 7,
    title: "CRM enrichment workflow",
    description: "Enrich existing CRM data with additional contact information",
    author: "CRM Team",
    category: "CRM Enhancement",
    useCase: "Data Enrichment",
    provider: "Clay + CRM",
    icon: Database,
    color: "bg-red-500",
    tags: ["CRM", "Enrichment", "Data"],
    difficulty: "Intermediate"
  },
  {
    id: 8,
    title: "Starry TAM with Find People",
    description: "Total Addressable Market analysis with people finding",
    author: "Growth Team",
    category: "Market Analysis", 
    useCase: "TAM Analysis",
    provider: "Clay + Find People",
    icon: Target,
    color: "bg-cyan-500",
    tags: ["TAM", "Market Analysis", "People"],
    difficulty: "Advanced"
  }
];

const categories = ["All", "Lead Generation", "Sales Prep", "Data Hygiene", "Data Collection", "Event Management", "Industry Specific", "CRM Enhancement", "Market Analysis"];
const useCases = ["All", "Local Business", "Discovery Calls", "Email Validation", "Web Scraping", "Event Scoring", "Entertainment", "Data Enrichment", "TAM Analysis"];
const difficulties = ["All", "Beginner", "Intermediate", "Advanced"];
const providers = ["All", "Clay + Openmart", "Autobound + Clay", "Clay", "Clay Chrome", "Clay + AI", "Custom", "Clay + CRM", "Clay + Find People"];

export default function AutomationLibrary() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedUseCase, setSelectedUseCase] = useState('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');
  const [selectedProvider, setSelectedProvider] = useState('All');

  const filteredTemplates = automationTemplates.filter(template => {
    const matchesSearch = template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || template.category === selectedCategory;
    const matchesUseCase = selectedUseCase === 'All' || template.useCase === selectedUseCase;
    const matchesDifficulty = selectedDifficulty === 'All' || template.difficulty === selectedDifficulty;
    const matchesProvider = selectedProvider === 'All' || template.provider === selectedProvider;

    return matchesSearch && matchesCategory && matchesUseCase && matchesDifficulty && matchesProvider;
  });

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategory('All');
    setSelectedUseCase('All');
    setSelectedDifficulty('All');
    setSelectedProvider('All');
  };

  return (
    <NewAppLayout fullWidth>
      <div>
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-6xl font-bold text-foreground mb-4">
            Automation Library
          </h1>
          <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
            Access our community's best resources to build powerful workflows leveraging the full-power of Clay and other tools
          </p>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col lg:flex-row gap-8 mb-8">
          {/* Left Sidebar - Filters */}
          <div className="lg:w-80 space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground">SEARCH</h3>
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={clearAllFilters}
                  className="text-primary hover:text-primary/80"
                >
                  Clear all
                </Button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-4">FILTERS</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Provider</label>
                  <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                    <SelectTrigger>
                      <SelectValue />
                      <ChevronDown className="w-4 h-4" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map(provider => (
                        <SelectItem key={provider} value={provider}>{provider}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Use case</label>
                  <Select value={selectedUseCase} onValueChange={setSelectedUseCase}>
                    <SelectTrigger>
                      <SelectValue />
                      <ChevronDown className="w-4 h-4" />
                    </SelectTrigger>
                    <SelectContent>
                      {useCases.map(useCase => (
                        <SelectItem key={useCase} value={useCase}>{useCase}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Difficulty</label>
                  <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
                    <SelectTrigger>
                      <SelectValue />
                      <ChevronDown className="w-4 h-4" />
                    </SelectTrigger>
                    <SelectContent>
                      {difficulties.map(difficulty => (
                        <SelectItem key={difficulty} value={difficulty}>{difficulty}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue />
                      <ChevronDown className="w-4 h-4" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Button className="w-full" size="lg">
              Submit an Automation <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Main Content - Templates Grid */}
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
              {filteredTemplates.map((template) => {
                const IconComponent = template.icon;
                return (
                  <Card key={template.id} className="group hover:shadow-lg transition-shadow cursor-pointer">
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        {/* Icon and Provider */}
                        <div className="flex items-start justify-between">
                          <div className={`w-12 h-12 ${template.color} rounded-lg flex items-center justify-center`}>
                            <IconComponent className="w-6 h-6 text-white" />
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {template.provider}
                          </Badge>
                        </div>

                        {/* Title and Description */}
                        <div>
                          <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                            {template.title}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-3">
                            {template.description}
                          </p>
                        </div>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1">
                          {template.tags.slice(0, 3).map((tag, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>

                        {/* Author and Difficulty */}
                        <div className="flex items-center justify-between pt-2 border-t">
                          <span className="text-xs text-muted-foreground">
                            By {template.author}
                          </span>
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${
                              template.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' :
                              template.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}
                          >
                            {template.difficulty}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {filteredTemplates.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No automations match your current filters.</p>
                <Button 
                  variant="link" 
                  onClick={clearAllFilters}
                  className="mt-2"
                >
                  Clear all filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </NewAppLayout>
  );
}